import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { seoActionDecision, seoGovernance } from "@/lib/seo/seo-os.functions";
import { SeoPage, Panel, Pill, Loading, Empty } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/publish-actions")({
  head: () => ({ meta: [{ title: "Approvals · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: ApprovalsPage,
});

const RISK_TONE: Record<string, string> = { high: "bad", medium: "warn", low: "good" };
const STATUS_TONE: Record<string, string> = {
  pending: "warn",
  approved: "good",
  staged: "good",
  applied: "good",
  rejected: "neutral",
  blocked: "bad",
  rolled_back: "neutral",
};

function ApprovalsPage() {
  const load = useServerFn(seoGovernance);
  const decide = useServerFn(seoActionDecision);
  const q = useQuery({ queryKey: ["seo-governance"], queryFn: () => load() });

  const act = useMutation({
    mutationFn: (payload: any) => decide({ data: payload }),
    onSuccess: () => {
      toast.success("Updated");
      q.refetch();
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not update"),
  });

  const d = q.data as any;

  return (
    <SeoPage
      title="Approvals and change history"
      description="Every change the SEO system wants to make, what it would replace, and the exact reason it is considered risky or safe. Nothing touching rates, eligibility, licensing or claims can be applied automatically."
    >
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="grid gap-4">
          <Panel title="Waiting for you">
            {d?.pending?.length ? (
              <div className="grid gap-3">
                {d.pending.map((a: any) => (
                  <ActionCard key={a.id} action={a} onDecide={(decision) => act.mutate({ id: a.id, decision })} />
                ))}
              </div>
            ) : (
              <Empty>Nothing is waiting for approval.</Empty>
            )}
          </Panel>

          <Panel title="Recent decisions">
            {d?.recent?.length ? (
              <div className="grid gap-2">
                {d.recent
                  .filter((a: any) => a.status !== "pending")
                  .slice(0, 40)
                  .map((a: any) => (
                    <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border p-3 text-sm">
                      <span className="font-medium">{a.target_path}</span>
                      <span className="text-muted-foreground">{a.action_type.replace(/_/g, " ")}</span>
                      <Pill tone={STATUS_TONE[a.status] ?? "neutral"}>{a.status.replace(/_/g, " ")}</Pill>
                      <div className="flex gap-2">
                        {a.applied_at ? (
                          <>
                            <Button size="sm" variant="outline" onClick={() => act.mutate({ id: a.id, decision: "evaluate" })}>
                              Measure impact
                            </Button>
                            {a.status !== "rolled_back" ? (
                              <Button size="sm" variant="ghost" onClick={() => act.mutate({ id: a.id, decision: "rollback" })}>
                                Undo
                              </Button>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <Empty>No history yet.</Empty>
            )}
          </Panel>
        </div>
      )}
    </SeoPage>
  );
}

function ActionCard({ action, onDecide }: { action: any; onDecide: (d: "approve" | "reject") => void }) {
  const terms: string[] = action.evidence?.sensitiveTerms ?? [];
  return (
    <div className="rounded-2xl border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold">{action.target_path}</p>
        <div className="flex gap-2">
          <Pill tone={RISK_TONE[action.risk] ?? "neutral"}>{`${action.risk} risk`}</Pill>
          <Pill>{action.action_type.replace(/_/g, " ")}</Pill>
        </div>
      </div>
      <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
        <div className="rounded-lg bg-muted/40 p-3">
          <p className="text-xs font-semibold uppercase text-muted-foreground">Currently</p>
          <p className="mt-1 break-words">{action.before_value ?? "— (not set)"}</p>
        </div>
        <div className="rounded-lg bg-muted/40 p-3">
          <p className="text-xs font-semibold uppercase text-muted-foreground">Proposed</p>
          <p className="mt-1 break-words">{action.after_value ?? "—"}</p>
        </div>
      </div>
      {terms.length ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Sensitive wording detected: {terms.slice(0, 8).join(", ")} — needs a human decision.
        </p>
      ) : null}
      {action.qa?.warnings?.length ? (
        <p className="mt-1 text-xs text-muted-foreground">{action.qa.warnings.join(" ")}</p>
      ) : null}
      <div className="mt-3 flex gap-2">
        <Button size="sm" onClick={() => onDecide("approve")}>
          Approve
        </Button>
        <Button size="sm" variant="outline" onClick={() => onDecide("reject")}>
          Reject
        </Button>
      </div>
    </div>
  );
}
